/**
 * MyRewardsModal - 我的奖励弹窗组件
 *
 * 展示用户已获得的奖励列表
 *
 * @module MyRewardsModal
 * @date 2026-01-04
 */

'use client'

import React, { useState, useRef, useEffect } from 'react'
import { cn } from '../../helpers/index.js'
import { BaseModal } from './BaseModal.js'

export type RewardStatus = 'Available' | 'Expired' | 'Used'

export interface Reward {
  /**
   * 奖励 ID
   */
  id: string

  /**
   * 奖励名称
   */
  name: string

  /**
   * 优惠码（可选）
   */
  code?: string

  /**
   * 状态
   */
  status: RewardStatus

  /**
   * 奖励时间
   */
  prizeTime?: string

  /**
   * 是否可复制码
   */
  showCopyCode?: boolean

  /**   * 是否可用
   */
  available?: boolean
}

export interface MyRewardsModalProps {
  /**
   * 是否显示弹窗
   */
  isOpen: boolean

  /**
   * 关闭弹窗回调
   */
  onClose: () => void

  /**
   * 弹窗标题
   * @default "My Rewards"
   */
  title?: string

  /**
   * 奖励列表
   */
  rewards: Reward[]

  /**
   * 复制码点击回调
   */
  onCopyCode?: (code: string) => void

  /**
   * 自定义类名
   */
  className?: string

  /**
   * 代码文本前缀
   * @default "CODE:"
   */
  codeText?: string

  /**
   * 复制按钮文本
   * @default "COPY"
   */
  copyText?: string

  /**
   * 复制成功后的按钮文本
   * @default "COPIED"
   */
  copiedText?: string

  /**
   * 奖励时间文本前缀
   * @default "Prize time:"
   */
  prizeText?: string

  /**
   * 空状态文案
   * @default "No rewards yet"
   */
  emptyText?: string

  /**
   * 主题模式
   * @default 'light'
   */
  theme?: 'light' | 'dark'
}

/**
 * MyRewardsModal 我的奖励弹窗组件
 */
export const MyRewardsModal: React.FC<MyRewardsModalProps> = ({
  isOpen,
  onClose,
  title = 'My Rewards',
  rewards,
  onCopyCode,
  className,
  codeText = 'CODE:',
  copyText = 'COPY',
  copiedText = 'COPIED',
  prizeText = 'Prize time:',
  emptyText = 'No rewards yet',
  theme = 'light',
}) => {
  // 追踪每个奖励的复制状态
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({})
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({})

  // 清理所有定时器
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(timer => clearTimeout(timer))
    }
  }, [])

  // 处理复制操作
  const handleCopy = (rewardId: string, code: string) => {
    // 清除该奖励的现有定时器
    if (timersRef.current[rewardId]) {
      clearTimeout(timersRef.current[rewardId])
    }

    navigator.clipboard.writeText(code)
    // 调用复制回调
    if (onCopyCode) onCopyCode?.(code)

    // 设置为已复制状态
    setCopiedStates(prev => ({ ...prev, [rewardId]: true }))

    // 3秒后恢复
    timersRef.current[rewardId] = setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [rewardId]: false }))
      delete timersRef.current[rewardId]
    }, 3000)
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-[480px] laptop:max-w-[520px]"
      background="bg-white"
      className={className}
      title={title}
      ariaLabelledBy="rewards-modal-title"
      theme={theme}
    >
      <div className="laptop:px-8 laptop:pb-8 p-4">
        {/* 奖励列表 */}
        <div className="flex max-h-[584px] flex-col gap-2 overflow-y-auto">
          {rewards.length === 0 ? (
            <p className="py-8 text-center text-[14px] text-gray-500">{emptyText}</p>
          ) : (
            rewards.map(reward => (
              <div
                key={reward.id}
                className={cn('rounded-box', 'relative flex-none overflow-hidden bg-[#F5F5F7]', 'px-4 py-6')}
              >
                <div className="tablet:text-[16px] tablet:flex items-center justify-between gap-2 text-[14px] font-[700]">
                  <div className="flex-1">
                    <p
                      className={cn(
                        'mb-1 font-[700] text-[#1D1D1F]',
                        reward?.available ? 'text-[#1D1D1F]' : 'text-[#767880]'
                      )}
                    >
                      {reward.name}
                    </p>
                    {reward.code && (
                      <div
                        className={cn(
                          'flex items-center gap-2',
                          reward?.available ? 'text-[#34C759]' : 'text-[#D1D1D1]'
                        )}
                      >
                        <p className="max-w-[200px] truncate">
                          {codeText} {reward.code}
                        </p>
                        {reward.showCopyCode && reward?.available && (
                          <button onClick={() => handleCopy(reward.id, reward.code!)} className="underline">
                            {copiedStates[reward.id] ? copiedText : copyText}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {reward.prizeTime && (
                    <div className={cn('tablet:block flex gap-1 text-[14px] font-[700] text-[#767880]')}>
                      <p>{prizeText}</p>
                      <p>{reward.prizeTime}</p>
                    </div>
                  )}

                  {/* 状态标签 */}
                  <span
                    className={cn(
                      'rounded-[0_8px] px-[6px] text-[14px] font-[700]',
                      'absolute right-0 top-0',
                      reward?.available ? 'bg-[#34C759] text-white' : 'bg-[#767880] text-white'
                    )}
                  >
                    {reward.status}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </BaseModal>
  )
}

export default MyRewardsModal
